import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { legacyProfileKeys, legacyProfileQueries } from '../api/legacy-profile-queries';
import { legacyProfileApi, type LegacyStatsQuery } from '../api/legacy-profile-api';

/**
 * The merchant's own profile (balance, business flags, contract).
 * Pass `newClientsFrom` (`YYYY-MM-DD`) where the `new_client_count` matters —
 * the dashboard sends the week start, as the old shell did.
 */
export const useLegacyProfile = (query?: { newClientsFrom?: string }) =>
  useQuery(legacyProfileQueries.me(query));

/** Day-bucketed order counts for the "Peak Days" chart. */
export const useLegacyStats = (query: LegacyStatsQuery) =>
  useQuery(legacyProfileQueries.stats(query));

/** The merchant's tariff — base rate, distance bands, demand coefficient. */
export const useLegacyPricing = () => useQuery(legacyProfileQueries.pricing());

/**
 * Customer reviews. Unpaginated for the dashboard card; pass
 * `{limit, skip}` for the full `/reviews` page.
 */
export const useLegacyRatings = (query?: { limit?: number; skip?: number }) =>
  useQuery(legacyProfileQueries.ratings(query));

/** Saved payment cards — the order form offers the DEFAULT one as "Card *NNNN". */
export const useLegacyCards = () => useQuery(legacyProfileQueries.cards());

/** Mutation hook to update merchant profile info through merchant-profile. */
export const useUpdateLegacyProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; logo_url: string }) =>
      legacyProfileApi.updateProfile(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: legacyProfileKeys.all });
    },
  });
};

/** Mutation hook to update integration callback data */
export const useUpdateLegacyCallback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { url: string; token: string }) =>
      legacyProfileApi.updateCallback(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: legacyProfileKeys.all });
    },
  });
};

/**
 * Mutation hook that mints a new integration token. The modal renders its own
 * pending/error states, so the global `MutationCache` error toast is
 * suppressed — otherwise a failure would toast AND render inline.
 */
export const useGenerateIntegrationToken = () =>
  useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: () => legacyProfileApi.generateIntegrationToken(),
  });

/** Paginated vendor invoices query */
export const useLegacyInvoices = (query: { limit: number; skip: number }) =>
  useQuery(legacyProfileQueries.invoices(query));
